import api from './axios';
import { ApiResponse } from '../types';

export type Player = {
  id: string;
  name: string;
  role: string;
  age: number | null;
  battingStyle: string;
  bowlingStyle: string | null;
  district: string;
  districtCode?: string;
  phone?: string;
  email?: string;
  thumbnail?: string | null;
  bio: string;
  currentTeam?: string;
  jersey_size?: string;
  cricheroes_profile_link?: string;
  playerId?: string;
};

// Transform backend response (snake_case) to frontend format (camelCase)
const transformPlayer = (backendPlayer: any): Player => {
  console.log('transformPlayer - Input:', backendPlayer);
  console.log('transformPlayer - player_id:', backendPlayer.player_id);
  console.log('transformPlayer - playing_role:', backendPlayer.playing_role);
  console.log('transformPlayer - district_code:', backendPlayer.district_code);
  console.log('transformPlayer - district_name:', backendPlayer.district_name);
  
  const transformed = {
    id: backendPlayer._id || backendPlayer.id,
    name: backendPlayer.name || '',
    // Map playing_role to role (backend uses playing_role)
    role: backendPlayer.playing_role || backendPlayer.role || '',
    age: backendPlayer.age !== undefined ? backendPlayer.age : null,
    battingStyle: backendPlayer.batting_style || backendPlayer.battingStyle || '',
    bowlingStyle: backendPlayer.bowling_style || backendPlayer.bowlingStyle || null,
    // Map district_code or district_name to district
    district: backendPlayer.district_name || backendPlayer.district || backendPlayer.district_code || '',
    districtCode: backendPlayer.district_code || backendPlayer.districtCode,
    phone: backendPlayer.phone || '',
    email: backendPlayer.email || '',
    thumbnail: backendPlayer.thumbnail || backendPlayer.profile_photo || null,
    bio: backendPlayer.bio || '',
    currentTeam: backendPlayer.current_team || backendPlayer.currentTeam,
    jersey_size: backendPlayer.jersey_size,
    cricheroes_profile_link: backendPlayer.cricheroes_profile_link,
    // Map player_id to playerId (critical for display)
    playerId: backendPlayer.player_id || backendPlayer.playerId || undefined,
  };
  
  console.log('transformPlayer - Output:', transformed);
  console.log('transformPlayer - playerId:', transformed.playerId);
  console.log('transformPlayer - role:', transformed.role);
  console.log('transformPlayer - district:', transformed.district);
  
  // Warn if playerId is missing
  if (!transformed.playerId) {
    console.warn('transformPlayer - WARNING: playerId is missing!', {
      input_player_id: backendPlayer.player_id,
      input_playerId: backendPlayer.playerId,
      transformed_playerId: transformed.playerId,
      allInputKeys: Object.keys(backendPlayer),
    });
  } else {
    console.log('transformPlayer - SUCCESS: playerId found:', transformed.playerId);
  }
  
  return transformed;
};

const transformPlayers = (players: any[]): Player[] => {
  return players.map(transformPlayer);
};

export type PlayerFilters = {
  role?: string;
  district?: string;
  search?: string;
  limit?: number;
  offset?: number;
};

export type District = {
  code: string;
  name: string;
};

export const getPlayers = async (filters?: PlayerFilters) => {
  const params = new URLSearchParams();
  if (filters?.role) params.append('role', filters.role);
  if (filters?.district) params.append('district', filters.district);
  if (filters?.search) params.append('search', filters.search);
  if (filters?.limit) params.append('limit', filters.limit.toString());
  if (filters?.offset) params.append('offset', filters.offset.toString());
  
  const queryString = params.toString();
  const url = `/api/v1/players${queryString ? `?${queryString}` : ''}`;
  
  try {
  const response = await api.get<ApiResponse<Player[]>>(url);
    
    console.log('getPlayers - Raw axios response:', response);
    console.log('getPlayers - response.data (server response body):', response.data);
    console.log('getPlayers - response.data.data (players array):', response.data?.data);
    console.log('getPlayers - response.data type:', typeof response.data);
    console.log('getPlayers - response.data isArray:', Array.isArray(response.data));
    
    let playersArray: any[] = [];
    
    // Backend response structure: { success: true, data: [...], message: "..." }
    // So response.data.data contains the players array
    if (response.data?.data && Array.isArray(response.data.data)) {
      console.log('getPlayers - Found players array in response.data.data, length:', response.data.data.length);
      playersArray = response.data.data;
    }
    // Fallback: Handle direct array: response.data is array (unlikely but possible)
    else if (Array.isArray(response.data)) {
      console.log('getPlayers - response.data is direct array, length:', response.data.length);
      playersArray = response.data;
    }
    // Fallback: Handle object with data property
    else if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      const dataValue = (response.data as any).data;
      if (Array.isArray(dataValue)) {
        console.log('getPlayers - Found data property with array, length:', dataValue.length);
        playersArray = dataValue;
      } else {
        console.warn('getPlayers - data property exists but is not an array:', dataValue);
      }
    }
    // Handle empty or unexpected structure
    else {
      console.warn('getPlayers - Unexpected response structure:', response.data);
      playersArray = [];
    }
    
    console.log('getPlayers - Players array before transform, length:', playersArray.length);
    if (playersArray.length > 0) {
      console.log('getPlayers - First player before transform:', playersArray[0]);
      console.log('getPlayers - First player player_id:', playersArray[0]?.player_id);
    }
    
    // Always transform, even if array is empty
    const transformedPlayers = transformPlayers(playersArray);
    
    console.log('getPlayers - Transformed players, length:', transformedPlayers.length);
    if (transformedPlayers.length > 0) {
      console.log('getPlayers - First player after transform:', transformedPlayers[0]);
      console.log('getPlayers - First player playerId:', transformedPlayers[0]?.playerId);
    }
    
    // Check if any players have playerId
    const playersWithId = transformedPlayers.filter(p => p.playerId);
    console.log('getPlayers - Players with playerId:', playersWithId.length, 'out of', transformedPlayers.length);
    if (playersWithId.length > 0) {
      console.log('getPlayers - Sample player with ID:', playersWithId[0]);
    } else {
      console.warn('getPlayers - WARNING: No players have playerId after transformation!');
    }
    
    // Return in the expected format: { success: true, data: [...], message: "..." }
    return {
      ...response.data,
      data: transformedPlayers,
    };
  } catch (error) {
    console.error('getPlayers - Error fetching players:', error);
    // Return empty array on error
    return {
      success: false,
      data: [],
      message: 'Failed to fetch players',
    };
  }
};

export const getPlayerById = async (playerId: string) => {
  try {
  const response = await api.get<ApiResponse<Player>>(`/api/v1/players/${playerId}`);
    
    console.log('getPlayerById - Raw response:', response);
    console.log('getPlayerById - response.data:', response.data);
    
    let playerData: any = null;
    
    // Handle nested data structure: response.data.data
    if (response.data?.data) {
      console.log('getPlayerById - Found nested data.data');
      playerData = response.data.data;
    }
    // Handle direct object: response.data is the player object
    else if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
      console.log('getPlayerById - response.data is direct object');
      playerData = response.data;
    }
    
    if (playerData) {
      console.log('getPlayerById - Player data before transform:', playerData);
      const transformed = transformPlayer(playerData);
      console.log('getPlayerById - Player data after transform:', transformed);
      console.log('getPlayerById - Transformed playerId:', transformed.playerId);
      
      return {
        ...response.data,
        data: transformed,
      };
    }
    
    console.warn('getPlayerById - No player data found in response');
  return response.data;
  } catch (error) {
    console.error('getPlayerById - Error fetching player:', error);
    throw error;
  }
};

export const getDistricts = async () => {
  const response = await api.get<ApiResponse<District[]>>('/api/v1/players/districts');
  return response.data;
};
